import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { isMobileOnly } from 'react-device-detect';
import { useDispatch, useSelector } from 'react-redux';
import { GetDirection } from 'shared';
import { isEmpty, get, isNull } from 'lodash-es';
import {
  BaseBox,
  FlexBox,
  Icons,
  SecondaryButton,
  Heading2,
  Heading4,
  PrimaryButton,
  CenterBox,
  Heading3,
  TertiaryButton,
} from '@docon/docon-ui-library';
import Slots from './Slots';
import Timings from './Timings';
import OtherNumbers from './OtherNumbers';
import TimingModal from './Modal/TimingModal';
import BookAppointmentModal from 'components/BookAppointment';
import { getOnlineSchedule } from 'redux/reducer/shift';
import { AppointmentBookIcon, ClinicIcon } from 'shared/icons';
import { bgColor, borderColor, colors } from 'utilities/colorConfig';
import { checkBookingEnable, getAddressString, getReadableDate } from 'utils';
import { cleverTapAnl } from 'utilities';
import { EventBookAppointment } from 'utilities/clevertapEvent';
import { emitEvent, isPeIosWebview } from 'utils/webview/webview';
import { useCookies } from 'react-cookie';
import { cookieNames } from 'utils/constants';

const Content = styled(BaseBox)`
  flex: 1;  
`;

const Heading = styled(Heading3)`
  font-weight: 600;
`;

const Row = styled(FlexBox)`
  padding: 12px 20px;
  gap: 0 20px;

  & + & {
    border-top: 1px solid ${borderColor};
  }
`;

const Footer = styled(Row)`
  gap: 12px;
  align-items: center;
  justify-content: flex-end;
  color: ${colors.primaryBlue};
`;

function Clinic({
  vcPrice: {
    flat_price = '',
  } = {},
  doctor = {},
  clinic = {},
  nextSlotDate = '',
}: any) {

  const {
    onlineScheduleByClinicId,
  } = useSelector(state => state.shift);
  const [showMobileNumber, setShowMobileNumber] = useState(false);
  const [openBookingModal, setOpenBookingModal] = useState<any>(false);
  const [showTimingModal, setShowTimingModal] = useState(false);
  const [activeSlot, setActiveSlot] = useState({});
  const [cookies, setCookies] = useCookies([cookieNames.VENDOR_COOKIE]);
  const isPharmeasyIosWebview = isPeIosWebview(cookies[cookieNames.VENDOR_COOKIE], navigator?.userAgent);

  const dispatch = useDispatch();

  // const nextSlot = nextAvailableSlotByClinicId[clinic.clinicID] || {};
  // const shifts = onlineScheduleByClinicId[clinic.clinicID]?.shifts || {};

  useEffect(() => {
    if (nextSlotDate && !openBookingModal) {
      dispatch(getOnlineSchedule({
        doctorID: doctor.doctorID ? doctor.doctorID : doctor.docID,
        date: nextSlotDate,
        clinicID: clinic.clinicID,
      }));
    }
  }, [nextSlotDate]);

  function handleCall() {
    if (clinic?.otherNumbers?.length)
      setShowMobileNumber(true);
    else
      if (isPharmeasyIosWebview) {
        const deeplink = `push.pharmeasy.clevertap://deeplink/open_dialer?phoneNumber=${clinic.number}`;
        emitEvent('onAction', {
          deeplink,
        });
      } else {
        window.location.href = `tel: ${clinic.number}`;
      }

  }

  function handleSlotClick({ slot = {} }: any) {
    setActiveSlot(slot);
    setOpenBookingModal(true);
  }

  const handleModalOpen = (modal: string) => () => {
    switch (modal) {
      case 'timings':
        return setShowTimingModal(true);
      case 'otherNumbers':
        return setShowMobileNumber(true);
      case 'appointmentBooking':
        cleverTapAnl.event({
          event_name: 'book_appointment',
          event: {
            page_name: 'doctor_profile',
            doctor_id: doctor.doctorID,
          },
        } as EventBookAppointment);
        return setOpenBookingModal(true);
      default:
        break;
    }
  };

  const handleModalClose = (modal: string) => () => {
    switch (modal) {
      case 'timings':
        return setShowTimingModal(false);
      case 'otherNumbers':
        return setShowMobileNumber(false);
      case 'appointmentBooking':
        setActiveSlot({});
        return setOpenBookingModal(false);
      default:
        break;
    }
  };

  const clinicAddress = getAddressString(clinic.address);
  const docId = doctor?.doctorID || doctor?.docID;
  const consultationFee = get(clinic, `doctorFees.${docId}.consultation`, null);

  return (
    <>
      <TimingModal
        isOpen={showTimingModal}
        clinic={clinic}
        onClose={handleModalClose('timings')}
      />

      {
        showMobileNumber ? (
          <OtherNumbers
            isOpen
            clinic={clinic}
            onClose={handleModalClose('otherNumbers')}
          />
        ) : null
      }

      {
        openBookingModal ? (
          <BookAppointmentModal
            isOpen
            slot={activeSlot}
            doctor={{ ...doctor, clinicID: clinic.clinicID }}
            onClose={handleModalClose('appointmentBooking')}
          />
        ) : null
      }

      <Row gap={'20px'}>
        <CenterBox size={45} bg={bgColor.warning} borderRadius={'50%'}>
          <ClinicIcon color={colors.white} width={25} />
        </CenterBox>
        <Content>
          <Heading2 title={clinic.name} fontWeight={500} />
          <Heading3 color={colors.secondaryColor} title={getAddressString(clinic.address)} />
        </Content>
      </Row>

      {
        isEmpty(onlineScheduleByClinicId[clinic.clinicID]) ? null : (
          <Row vertical verticalSpace={0}>
            <Heading font-weight title={`Next available: ${getReadableDate(nextSlotDate)}`} />
            <Slots
              doctor={doctor}
              clinic={clinic}
              nextSlotDate={nextSlotDate}
              onlineSchedule={onlineScheduleByClinicId[clinic.clinicID]}
              onClick={handleSlotClick}
            />
          </Row>
        )
      }

      <Row>
        <BaseBox flex={isMobileOnly ? '100%' : 1}>
          <Heading title='Address' />
          <Heading4 title={clinicAddress} />
          <GetDirection address={clinicAddress} />
        </BaseBox>

        <BaseBox flex={1} mt={isMobileOnly ? '12px' : 0}>
          <Timings shifts={clinic.shifts} />
          <TertiaryButton
            hidden={isEmpty(clinic.shifts)}
            m={'0 !important'}
            p={'0 !important'}
            title='View all timings'
            onClick={handleModalOpen('timings')}
          />
        </BaseBox>
      </Row>

      <Row vertical verticalSpace={5} hidden={(isNull(consultationFee) && isNull(flat_price))}>
        <Heading title='Consultation' />
        <Heading4 title={isNull(consultationFee) ? '' : `In-Clinic Consult: ₹ ${consultationFee}`} />
        <Heading4 title={flat_price ? `Video Consult: ₹ ${flat_price || ''}` : ''} />
      </Row>

      <Footer>
        {/* <Heading4
          color={colors.primaryBlue}
          title={showMobileNumber ? `+91 ${clinic.number} ${clinic.otherNumbers?.join(', ') || ''}` : ''}
        /> */}
        <SecondaryButton
          icon={<Icons.PhoneIcon width={16} />}
          title='Call'
          onClick={handleCall}
        />

        <PrimaryButton
          hidden={!checkBookingEnable(clinic)}
          icon={<AppointmentBookIcon width={16} />}
          title={'Book'}
          onClick={handleModalOpen('appointmentBooking')}
        />
      </Footer>
    </>
  );
}

export default Clinic;